import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarBlank, PaperPlaneTilt } from "@phosphor-icons/react";

const DOW = ["Sat", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri"];

function weekStartSat(base) {
  const x = new Date(base); x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() - 6 + 7) % 7));
  return x;
}
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function ScheduleCard() {
  const [data, setData] = useState({ shifts: [], recurring: [] });
  const [weeks, setWeeks] = useState(1);

  const load = useCallback(async () => {
    const r = await api.get("/schedules");
    setData(r.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const start = weekStartSat(new Date());
  const days = [];
  for (let i = 0; i < 7 * weeks; i++) {
    const d = new Date(start); d.setDate(start.getDate() + i);
    const dateStr = iso(d);
    const pyWeekday = (d.getDay() + 6) % 7; // 0=Mon..6=Sun
    const items = [
      ...data.shifts.filter((s) => s.date === dateStr),
      ...data.recurring.filter((r) => r.weekday === pyWeekday).map((r) => ({ ...r, recurring: true })),
    ].sort((a, b) => (a.start || "").localeCompare(b.start || ""));
    days.push({ d, dateStr, items });
  }

  return (
    <Card className="rounded-2xl border-2 border-border p-0 overflow-hidden shadow-[6px_6px_0_0_rgba(0,0,0,0.08)]" data-testid="schedule-card">
      <div className="p-5 border-b-2 border-border flex items-center justify-between bg-primary text-primary-foreground">
        <div className="flex items-center gap-2 font-display font-bold text-lg tracking-tight"><CalendarBlank size={20} weight="bold" /> My Schedule</div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-white/15 rounded-full p-0.5" data-testid="schedule-week-toggle">
            {[["Week", 1], ["2 Weeks", 2]].map(([label, w]) => (
              <button key={w} onClick={() => setWeeks(w)} className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${weeks === w ? "bg-white text-primary" : "text-white/80"}`} data-testid={`schedule-weeks-${w}`}>{label}</button>
            ))}
          </div>
          <RequestChangeDialog />
        </div>
      </div>
      <div className="divide-y divide-border">
        {days.map(({ d, dateStr, items }) => {
          const today = iso(new Date()) === dateStr;
          return (
            <div key={dateStr} className={`flex items-start gap-4 px-5 py-3 ${today ? "bg-primary/5" : ""}`} data-testid={`schedule-day-${dateStr}`}>
              <div className="w-12 shrink-0 text-center">
                <div className="text-xs font-bold uppercase text-muted-foreground">{DOW[(d.getDay() - 6 + 7) % 7]}</div>
                <div className="font-display font-bold text-xl">{d.getDate()}</div>
              </div>
              <div className="flex-1 min-w-0 py-1">
                {items.length === 0 ? (
                  <span className="text-sm text-muted-foreground">—</span>
                ) : items.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 mb-1 last:mb-0">
                    <span className="font-mono text-sm font-bold text-primary">{s.start}–{s.end}</span>
                    {s.note && <span className="text-sm text-muted-foreground truncate">{s.note}</span>}
                    {s.recurring && <span className="text-[0.6rem] uppercase tracking-wider bg-muted px-1.5 py-0.5 rounded">weekly</span>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function RequestChangeDialog() {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const send = async () => {
    if (!msg.trim()) return;
    try {
      await api.post("/schedules/request", { message: msg });
      toast.success("Request sent to admin");
      setOpen(false); setMsg("");
    } catch { toast.error("Could not send request"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full bg-white/15 hover:bg-white/25 text-white h-8" data-testid="request-change-button"><PaperPlaneTilt size={14} className="mr-1.5" /> Request</Button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Request a schedule change</DialogTitle></DialogHeader>
        <Input className="rounded-lg" placeholder="e.g. Please move my Tuesday shift to Wednesday" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="request-change-input" />
        <DialogFooter><Button className="rounded-lg" onClick={send} data-testid="request-change-submit">Send request</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
